import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { Button, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, makeStyles } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import InfoItem from "../../../custom/InfoItem";
import ActionDialog from "../ActionDialog";
import InviteStatusBadge from "./InviteStatusBadge";
import { resendInvite, revokeInvite } from "../../../actions/Invite";
import { showErrorMessage, showSuccessMessage } from "../../../actions/Alert";
import { getMe } from "../../../actions/User";
import { fullDateFormatter, INTITE_STATUS } from "../../../util";
import LocalTimeDisplay from "../LocalTimeDisplay";

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  const backendMessage = error?.response?.data?.message;
  if (status === 401 || status === 403) return "app.inviteToken.create.permissionDenied";
  return backendMessage || "app.server.connect.error";
};

const ExpirationNotice = ({ remainingTime }) => {
  if (!remainingTime) return null;

  return (
    <>
      <Typography variant="caption" color="textSecondary">
        <FormattedMessage id="app.invite.remaining" />
      </Typography>
      <LocalTimeDisplay time={remainingTime} />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  revokeButton: {
    height: "100%",
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  revokeConfirmButton: {
    color: theme.palette.error.main,
  },
}));

const InviteDetailsDialog = ({ open, invite, onClose, onChanged, canManage, showAgent }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);

  const status = invite.status;

  const statusDate = useMemo(() => {
    switch (status) {
      case INTITE_STATUS.REVOKED:
        return { caption: "app.invite.revokedAt", value: invite.revokeAt || invite.revokedAt };
      case INTITE_STATUS.USED:
        return { caption: "app.invite.usedAt", value: invite.usedAt };
      default:
        return { caption: "app.invite.expiresAt", value: invite.expiresAt };
    }
  }, [status, invite]);

  const handleRevoke = async () => {
    setLoading(true);
    try {
      const updated = await revokeInvite(invite.id);
      dispatch(showSuccessMessage("app.invite.revoke.success"));
      setConfirmRevokeOpen(false);
      onChanged({ ...invite, ...updated });
    } catch (error) {
      if (error?.response?.status === 400) dispatch(getMe());
      dispatch(showErrorMessage(getErrorMessage(error)));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const data = await resendInvite(invite.id);
      const updatedInvite = data && typeof data === "object" ? { ...invite, ...data } : { ...invite, resentAt: new Date().toISOString() };
      dispatch(showSuccessMessage("app.invite.resend.success"));
      onChanged(updatedInvite);
    } catch (error) {
      if (error?.response?.status === 400) dispatch(getMe());
      dispatch(showErrorMessage(getErrorMessage(error)));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (invite.inviteUrl) {
      navigator.clipboard.writeText(invite.inviteUrl);
      dispatch(showSuccessMessage("app.invite.copy.success"));
      return;
    }

    dispatch(showErrorMessage("app.invite.copy.unavailable"));
  };

  return (
    <ActionDialog title="app.invite.details.title" open={open} onClose={onClose} enableConfirmButton={false}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.headerRow}>
            <Typography variant="h6">{invite.name}</Typography>
            <InviteStatusBadge status={status} />
          </Grid>

          <Grid item xs={12}>
            <InfoItem caption={<FormattedMessage id="app.email" />} text={invite.email} />
          </Grid>

          {invite?.phone && (
            <Grid item xs={12}>
              <InfoItem caption={<FormattedMessage id="app.phone" />} text={invite.phone} />
            </Grid>
          )}

          <Grid item xs={12} container spacing={2}>
            <Grid item xs={6}>
              <InfoItem caption={<FormattedMessage id="app.invite.createdAt" />} text={fullDateFormatter(invite.createdAt) || "-"} />
            </Grid>
            <Grid item xs={6}>
              <InfoItem
                caption={<FormattedMessage id={statusDate.caption} />}
                text={fullDateFormatter(statusDate.value) || "-"}
              />
            </Grid>
          </Grid>

          {invite.resentAt && (
            <Grid item xs={12} sm={6}>
              <InfoItem caption={<FormattedMessage id="app.invite.resendAt" />} text={fullDateFormatter(invite.resentAt)} />
            </Grid>
          )}

          {status === INTITE_STATUS.USED && invite.userId && (
            <Grid item xs={12} sm={6}>
              <InfoItem
                caption={<FormattedMessage id="app.invite.profile.label" />}
                text={
                  <Link to={`/customer-detail/${invite.userId}`}>
                    <FormattedMessage id="app.invite.profile.link" />
                  </Link>
                }
              />
            </Grid>
          )}

          {showAgent && (
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <InfoItem
                  caption={<FormattedMessage id="app.invite.sentBy" />}
                  text={invite.agent?.name ? `${invite.agent.name} (${invite.agent.id})` : "-"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem caption={<FormattedMessage id="app.invite.sentBy.email" />} text={invite.agent?.email || "-"} />
              </Grid>
            </Grid>
          )}

          {invite.isActive && (
            <Grid item xs={12}>
              <ExpirationNotice remainingTime={invite.remainingTime} />
            </Grid>
          )}

          {canManage && invite.isActive && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  className={classes.revokeButton}
                  onClick={() => setConfirmRevokeOpen(true)}
                >
                  <FormattedMessage id="app.invite.revoke" />
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" color="primary" disabled={loading} onClick={handleResend}>
                  <FormattedMessage id="app.invite.resend" />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FileCopyIcon />}
                  disabled={loading}
                  onClick={handleCopy}
                >
                  <FormattedMessage id="app.invite.copyLink" />
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>

      <Dialog open={confirmRevokeOpen} onClose={() => setConfirmRevokeOpen(false)}>
        <DialogTitle>
          <FormattedMessage id="app.confirm.question" />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage id="app.invite.revoke.confirm" />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevokeOpen(false)} disabled={loading}>
            <FormattedMessage id="app.cancel" />
          </Button>
          <Button onClick={handleRevoke} className={classes.revokeConfirmButton} disabled={loading}>
            <FormattedMessage id="app.invite.revoke" />
          </Button>
        </DialogActions>
      </Dialog>

    </ActionDialog>
  );
};

export default InviteDetailsDialog;
